import { Archive, Calendar, MoreVertical, Pencil, Trash2 } from "lucide-react";
import { CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useTranslation } from "@/hooks/useTranslation";
import { USER_ROUTES } from "@/routes";
import { useNavigate } from "react-router-dom";
import { formatDateShort } from "@/utils/eventUtils";
import { useEffect, useRef } from "react";

interface EventCardHeaderProps {
  title: string;
  /** null while the event date is not decided yet (JUB-154). */
  date: string | null;
  eventType: string;
  /** JUB-169: labels of the enabled moments, in display order. */
  subEvents: string[];
  eventUuid: string;
  currentLocale: string;
  isMenuOpen: boolean;
  onMenuToggle: () => void;
  onMenuClose: () => void;
  currentEventsCount: number;
  onDelete: (eventUuid: string) => void;
  onArchive?: (eventUuid: string) => void;
  /**
   * JUB-157: a paid event's main action is "Archive"; deleting it sits in a
   * danger zone (it still goes to the Trash for 30 days first). A free event
   * is simply moved to the Trash.
   */
  isPaid?: boolean;
  isLive?: boolean;
}

export function EventCardHeader({
  title,
  date,
  eventType,
  subEvents,
  eventUuid,
  currentLocale,
  isMenuOpen,
  onMenuToggle,
  onMenuClose,
  onDelete,
  onArchive,
  isPaid,
  isLive,
}: EventCardHeaderProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onMenuClose();
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen, onMenuClose]);

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    onMenuClose();
    navigate(USER_ROUTES.event(currentLocale, eventUuid));
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onMenuClose();
    onDelete(eventUuid);
  };

  const handleArchive = (e: React.MouseEvent) => {
    e.stopPropagation();
    onMenuClose();
    onArchive?.(eventUuid);
  };

  return (
    <CardHeader className="pb-4 bg-gradient-to-r from-pink-500 to-rose-500 dark:from-pink-600 dark:to-rose-600 border-b border-pink-400 dark:border-pink-700">
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <CardTitle className="text-xl mb-2 font-bold text-white">
            {title}
          </CardTitle>
          <CardDescription className="text-sm font-medium text-white/90 flex items-center gap-2 flex-wrap">
            <Calendar className="h-3.5 w-3.5" />
            {date ? formatDateShort(date, currentLocale) : t('events.dateTbd')} • <span data-testid="event-card-type">{eventType}</span>
            <span
              data-testid="event-card-live-indicator"
              className="inline-flex items-center gap-1 rounded-full bg-white/20 backdrop-blur-sm px-2 py-0.5 text-xs font-semibold text-white"
            >
              <span
                className={
                  isLive
                    ? "h-1.5 w-1.5 rounded-full bg-green-300"
                    : "h-1.5 w-1.5 rounded-full bg-amber-300"
                }
              />
              {isLive ? t('events.published') : t('events.notPublished')}
            </span>
          </CardDescription>
        </div>
        <div className="relative" ref={menuRef}>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 hover:bg-white/20 rounded-full transition-colors text-white"
            data-testid="event-card-menu-button"
            onClick={(e) => {
              e.stopPropagation();
              onMenuToggle();
            }}
          >
            <MoreVertical className="h-4 w-4" />
          </Button>
          {isMenuOpen && (
            <div className="absolute right-0 top-full mt-2 z-50 w-56 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg">
              <div className="p-1">
                <button
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
                  onClick={handleEdit}
                >
                  <Pencil className="h-4 w-4" />
                  {t('events.edit')}
                </button>
                {onArchive && (
                  <button
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
                    data-testid="archive-button"
                    onClick={handleArchive}
                  >
                    <Archive className="h-4 w-4" />
                    {t('events.archive.action')}
                  </button>
                )}
                {isPaid && (
                  <p className="mt-1 border-t border-gray-200 dark:border-gray-700 px-3 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-red-600/80 dark:text-red-400/80">
                    {t('events.dangerZone.title')}
                  </p>
                )}
                <button
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30 rounded-md transition-colors"
                  data-testid="delete-button"
                  onClick={handleDelete}
                >
                  <Trash2 className="h-4 w-4" />
                  {isPaid ? t('events.dangerZone.deletePermanently') : t('events.dangerZone.moveToTrash')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      {/* JUB-169: the event's enabled moments, already labelled (custom title or localized default) */}
      {subEvents.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="event-card-moments">
          {subEvents.map((label, idx) => (
            <span
              key={`${idx}-${label}`}
              data-testid="event-card-moment"
              className="px-3 py-1 rounded-full bg-white/20 backdrop-blur-sm text-white text-sm font-medium"
            >
              {label}
            </span>
          ))}
        </div>
      )}
    </CardHeader>
  );
}

